<script setup lang="ts">
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import { default as PMessage } from 'primevue/message'
import { default as PButton } from 'primevue/button'
import { default as PProgressSpinner } from 'primevue/progressspinner'
import { default as PInputText } from 'primevue/inputtext'
import { useSchoolPeriodUserStore } from '@/presentation/stores/school/schoolPeriodUser.ts'
import type School from '@/core/domain/school/core/School.ts'
import { computed, onMounted, type Ref, ref, watch } from 'vue'
import { useSchoolPeriodStore } from '@/presentation/stores/school/schoolPeriod.ts'
import { storeToRefs } from 'pinia'
import type { SchoolPeriodUser } from '@/core/domain/school/user/SchoolPeriodUser.ts'
import { useToast } from 'primevue/usetoast'
import { useSchoolStore } from '@/presentation/stores/school/school.ts'
import { useSchoolPeriodUsersTable } from '@/presentation/composables/school/users/useSchoolPeriodUsersTable.ts'
import CSchoolPeriodUsersTable from '@/presentation/components/schools/CSchoolPeriodUsersTable.vue'
import CSchoolPeriodUsersUpdate from '@/presentation/components/schools/CSchoolPeriodUsersUpdate.vue'
import { SchoolPeriodUserInput } from '@/core/domain/school/user/SchoolPeriodUser.ts'
import { UserRole } from '@/core/domain/resources/enums/UserRole.ts'

// Composables
const toast = useToast()
const schoolPeriodUserStore = useSchoolPeriodUserStore()
const schoolPeriodStore = useSchoolPeriodStore()
const schoolStore = useSchoolStore()

// Store refs
const { students, schoolPeriodUserStoreIsLoading, schoolPeriodUserStoreError, needsFreshData } =
  storeToRefs(schoolPeriodUserStore)
const { selectedSchoolPeriod } = storeToRefs(schoolPeriodStore)
const { selectedSchool } = storeToRefs(schoolStore)

// Local state
const localLoading = ref(false)
const localError = ref('')

// État d'édition
interface EditUser {
  open: boolean
  user: SchoolPeriodUserInput | null
  isCreation: boolean
}

const editUser: Ref<EditUser> = ref({
  open: false,
  user: null,
  isCreation: false
})

// Gestion du tableau via le composable
const { searchQuery, processUsers, resetSearch } = useSchoolPeriodUsersTable()

// Props
const props = defineProps<{
  school: School
}>()

// Computed
const isLoading = computed(() => localLoading.value || schoolPeriodUserStoreIsLoading.value)

const hasError = computed(
  () =>
    localError.value !== '' ||
    (schoolPeriodUserStoreError.value && schoolPeriodUserStoreError.value.hasError)
)

const errorMessage = computed(
  () =>
    localError.value ||
    (schoolPeriodUserStoreError.value?.hasError ? schoolPeriodUserStoreError.value.message : '')
)

const studentCount = computed(() => students.value.length)
const hasStudents = computed(() => studentCount.value > 0)
const canAddStudent = computed(
  () => !isLoading.value && selectedSchoolPeriod.value !== null && props.school?.id
)

// Étudiants filtrés et triés
const processedStudents = computed(() => processUsers(students.value))
const filteredStudentCount = computed(() => processedStudents.value.length)

// Methods
/**
 * Charge les élèves de la période scolaire
 * @param forceRefresh Force le rechargement des données même si déjà présentes
 */
async function loadStudents(forceRefresh = false) {
  if (!selectedSchoolPeriod.value || !props.school?.id) return

  // Éviter les rechargements inutiles si les données sont déjà chargées
  if (!forceRefresh && students.value.length > 0 && !needsFreshData.value) {
    return
  }

  try {
    localLoading.value = true
    localError.value = ''

    await schoolPeriodUserStore.fetchAllSchoolPeriodUsersForSchool(
      props.school.id,
      selectedSchoolPeriod.value.schoolPeriodId,
      forceRefresh
    )
  } catch (err) {
    localError.value = err instanceof Error ? err.message : 'Erreur lors du chargement des élèves'

    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: localError.value,
      life: 5000
    })

    console.error('Erreur lors du chargement des élèves:', err)
  } finally {
    localLoading.value = false
  }
}

/**
 * Prépare l'édition d'un étudiant
 * @param user Étudiant à modifier (ou undefined pour création)
 */
async function startUserEdit(user?: SchoolPeriodUser) {
  if (!selectedSchoolPeriod.value || !selectedSchool.value) return

  try {
    localLoading.value = true
    localError.value = ''

    if (user) {
      // Mode édition
      editUser.value.user = await schoolPeriodUserStore.prepareSchoolPeriodUserEdit(
        selectedSchool.value.id,
        selectedSchoolPeriod.value.schoolPeriodId,
        user
      )
      editUser.value.isCreation = false
    } else {
      // Mode création - Forcer le rôle étudiant
      editUser.value.user = new SchoolPeriodUserInput()
      editUser.value.user.schoolPeriodId = selectedSchoolPeriod.value.schoolPeriodId
      editUser.value.user.roles = [UserRole.SchoolValues.STUDENT]
      editUser.value.isCreation = true
    }

    editUser.value.open = true
  } catch (err) {
    localError.value =
      err instanceof Error ? err.message : "Erreur lors de la préparation de l'édition"

    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: localError.value,
      life: 5000
    })

    console.error("Erreur lors de la préparation de l'édition:", err)
  } finally {
    localLoading.value = false
  }
}

/**
 * Ferme l'éditeur d'utilisateur
 */
function closeUserEdit() {
  editUser.value.open = false
  editUser.value.user = null

  // Recharger les étudiants pour refléter les changements
  loadStudents(true)
}

// Watchers
watch(selectedSchoolPeriod, async (newValue) => {
  if (newValue) {
    await loadStudents(true)
  } else {
    schoolPeriodUserStore.reset()
  }
})

// Lifecycle hooks
onMounted(async () => {
  // Les périodes scolaires sont déjà chargées par le router guard
  // Les données du tab sont pré-chargées par le composant parent (VSchoolsSchool)
  // On ne charge que si les données ne sont pas déjà présentes
  if (selectedSchoolPeriod.value && students.value.length === 0) {
    await loadStudents()
  }
})
</script>

<template>
  <!-- Modal d'édition d'utilisateur -->
  <CSchoolPeriodUsersUpdate
    v-if="editUser.open && editUser.user"
    :is-create="editUser.isCreation"
    :school="school"
    :allowed-roles="[UserRole.SchoolValues.STUDENT]"
    v-model="editUser.user"
    @close="closeUserEdit()"
  />

  <!-- Liste des élèves -->
  <CBaseBlock :toggleable="false" title="Élèves" v-else>
    <!-- Message quand aucune période n'est sélectionnée -->
    <p-message v-if="!selectedSchoolPeriod" severity="info" class="mb-4 w-full">
      Veuillez sélectionner une période scolaire pour voir les élèves.
    </p-message>

    <!-- Contenu principal -->
    <div v-else class="col-span-12 flex flex-col items-start gap-4">
      <!-- Message d'erreur -->
      <p-message v-if="hasError" severity="error" :text="errorMessage" class="mb-2 w-full" />

      <!-- En-tête avec boutons d'ajout et compteur -->
      <div class="flex w-full items-center justify-between">
        <div class="flex gap-2">
          <p-button
            label="Ajouter un élève"
            icon="pi pi-plus"
            severity="primary"
            size="small"
            @click="startUserEdit()"
            :disabled="!canAddStudent"
          />
        </div>

        <div v-if="isLoading" class="flex items-center">
          <p-progress-spinner style="width: 1.5rem; height: 1.5rem" />
          <span class="ml-2">Chargement...</span>
        </div>

        <div v-else-if="hasStudents" class="flex items-center gap-2">
          <span class="text-surface-500 dark:text-surface-300 text-sm">
            {{ filteredStudentCount }} élève(s)
            {{ searchQuery ? 'trouvé(s)' : 'au total' }}
          </span>

          <p-button
            v-if="searchQuery"
            icon="pi pi-times"
            text
            size="small"
            @click="resetSearch"
            tooltip="Effacer la recherche"
          />

          <p-button
            icon="pi pi-refresh"
            text
            size="small"
            @click="() => loadStudents(true)"
            tooltip="Actualiser"
          />
        </div>
      </div>

      <!-- Barre de recherche -->
      <div v-if="hasStudents" class="w-full">
        <span class="p-input-icon-left w-full md:w-96">
          <i class="pi pi-search" />
          <p-input-text
            v-model="searchQuery"
            placeholder="Rechercher un élève par nom, email ou nom d'utilisateur..."
            class="w-full"
          />
        </span>
      </div>

      <!-- État vide -->
      <div
        v-if="!isLoading && !hasStudents"
        class="flex w-full flex-col items-center justify-center p-8"
      >
        <i class="pi pi-graduation-cap text-surface-500 mb-4 text-4xl"></i>
        <h3 class="mt-0 mb-2">Aucun élève inscrit</h3>
        <p class="text-surface-900 text-center">
          Cette période scolaire ne contient pas encore d'élèves. Les élèves peuvent s'inscrire avec
          un code classe.
        </p>
      </div>

      <!-- Tableau des élèves -->
      <CSchoolPeriodUsersTable
        v-else-if="hasStudents"
        :users="processedStudents"
        :is-loading="isLoading"
        @edit-user="startUserEdit"
      />
    </div>
  </CBaseBlock>
</template>
